import React, { useEffect, useState } from "react";
import { Button, createStyles, makeStyles, Modal, TextField } from "@material-ui/core";
import styles from "./TemplateCreateModal.module.css";
import { FormControl } from "@material-ui/core";
import { ExtractFileName } from "../../../utils/ObjectUtils";
import OnLeaveDialog from "../../../common/components/dialog/OnLeaveDialog";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { UpdateTemplate } from "../../store/actions/TemplateActions";
import PuiTypography from "../../../components/elements/PuiTypography";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";
import { t } from "i18next";

const useStyles = makeStyles(() =>
    createStyles({
        btnStyle: {
            fontFamily: "noto-sans-cjk-jp",
            width: "calc((100% - 106px) * 0.8)",
            position: "absolute",
            height: "40px",
            bottom: "42px",
            left: "calc(53px + ((100% - 106px) * 0.2))",
            borderRadius: "20px",
            backgroundColor: "var(--studio-btn-bg)",
            color: "var(--gray-77)!important",
            "&:hover": { backgroundColor: "#57d3e3" },
        },
        btnStyleActive: {
            color: "var(--white)!important",
        },
        input: {
            width: "100%",
            "& input": {
                padding: "0 0 0 20px",
                height: "40px",
                backgroundColor: "#FFFFFF0D",
                fontFamily: "noto-sans-cjk-jp",
                color: "var(--primary-font)",
                borderRadius: "5px",
            },
            "& .MuiInput-underline:before": {
                borderBottom: "none",
            },
            "& .MuiInput-underline:after": {
                borderBottom: "none",
            },
            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottom: "none",
            },
        },
        select: {
            width: "100%",
            height: "40px",
            "& .MuiInput-underline:before": {
                display: "none",
            },
        },
    })
);

function TemplateCreateModal() {
    const classes = useStyles();
    const dispatch = useDispatch();

    // Storeからデータ取得
    const { user, isLoading } = useSelector((state: AppState) => ({
        user: state.user.user,
        isLoading: state.template.isLoading,
    }));

    // ユーザ入力保存確認
    const [isSaved, setIsSaved] = useState(true);
    // 確認ダイアログ表示
    const [openDialog, setOpenDialog] = React.useState(false);

    // Addボタンを押下の場合、
    const [openTemplateCreateModal, setOpenTemplateCreateModal] = useState(false);
    const handleAddButton = () => {
        setOpenTemplateCreateModal(true);
    };
    const CloseTemplateCreateModal = () => {
        if (isSaved) {
            // 入力中の値がない場合、モーダルを閉じる
            setOpenTemplateCreateModal(false);
        } else {
            // 入力中の値がある場合、確認ダイアログを表示する
            setOpenDialog(true);
        }
        setIsOpenSelect(false);
    };

    // テンプレートアップロードテキスト
    const [uploadTempleFile, setUploadTempleFile] = useState<File | null>(null);
    const [textUploadTemplate, ChangeTextUploadTemplate] = useState(
        t("アップロードするファイルを選択してください")
    );

    const UploadTemplateFile = (event: any) => {
        if (event.target.files.length === 1) {
            ChangeTextUploadTemplate(event.target.files[0].name);
            setUploadTempleFile(event.target.files[0]);
            setUploadTemplateName(ExtractFileName(event.target.files[0].name));
            setIsSaved(false);
        }
    };

    // テンプレート名称
    const [uploadTemplateName, setUploadTemplateName] = useState("");
    const ChangeName = (event: any) => {
        setUploadTemplateName(event.target.value);
        setIsSaved(false);
    };

    // テンプレート種別
    const [templateType, setTemplateType] = useState<string>("OP");
    const selectTemplateType = (type: string) => {
        setTemplateType(type);
        setIsSaved(false);
        setIsOpenSelect(false);
    };

    // 確認ダイアログの処理
    // 確認ダイアログ閉じる処理
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    // 確認ダイアログキャンセル処理
    const handleCancel = () => {
        handleCloseDialog();
    };

    // handleConfirmイベントでダイアログボックスの非表示後、フォルダー作成・フォルダー名変更モーダル非表示フラグ
    const [closeConfirmModal, isCloseConfirmModal] = useState(false);
    // 確認ダイアログの確認処理
    const handleConfirm = () => {
        // 入力した値をリセット
        ResetTemplateInfo();

        // 確認ダイアログを閉じる
        handleCloseDialog();
        isCloseConfirmModal(true);
    };

    // テンプレート作成ボタンを押下の場合、
    const [isTemplateCreated, setIsTemplateCreated] = useState(false);
    const handleCreateTemplate = () => {
        if (user && uploadTempleFile !== null) {
            dispatch(
                UpdateTemplate(
                    uploadTempleFile,
                    0,
                    uploadTemplateName,
                    templateType,
                    Number(user.id)
                )
            );
        }
        // 入力値リセット
        ResetTemplateInfo();
        setIsTemplateCreated(true);
    };

    // 入力値リセット
    const ResetTemplateInfo = () => {
        setIsSaved(true);
        setUploadTempleFile(null);
        ChangeTextUploadTemplate(t("アップロードするファイルを選択してください"));
        setUploadTemplateName("");
        setTemplateType("OP");
    };

    // 登録ボタンの有無効制御
    const [isBtnActive, setIsBtnActive] = useState(false);
    useEffect(() => {
        if (!isSaved && uploadTempleFile !== null && uploadTemplateName !== "") {
            setIsBtnActive(false);
        } else {
            setIsBtnActive(true);
        }
    }, [isSaved, uploadTempleFile, uploadTemplateName]);

    useEffect(() => {
        // モーダルを閉じる
        if (closeConfirmModal) CloseTemplateCreateModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closeConfirmModal, openDialog]);

    // テンプレート作成が終わった後、モーダルを閉じる
    useEffect(() => {
        if (isTemplateCreated) CloseTemplateCreateModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isTemplateCreated]);

    const [isOpenSelect, setIsOpenSelect] = useState<boolean>(false);

    return (
        <>
            <Button className={styles.addBtn} onClick={handleAddButton}>
                {t("テンプレート追加")}
            </Button>
            <Modal
                className={styles.addTimelinePage}
                open={openTemplateCreateModal}
                onClose={CloseTemplateCreateModal}
            >
                <div className={styles.root}>
                    <ul className={styles.inputForm}>
                        <FormControl>
                            <form name="addTemplateForm" id="addTemplateForm">
                                {/* テンプレート */}
                                <li className={styles.columnStyle} style={{ marginBottom: "20px" }}>
                                    <div className={styles.formHelperText}>
                                        <PuiTypography> {t("ファイル")}</PuiTypography>
                                    </div>
                                    <div className={styles.form}>
                                        <label className={styles.uploadButton}>
                                            <input
                                                id="browserTemplateUploader"
                                                multiple
                                                type="file"
                                                accept="video/*"
                                                onChange={UploadTemplateFile}
                                                className={styles.fileDisplay}
                                            />
                                            <PuiTypography>{textUploadTemplate}</PuiTypography>
                                        </label>
                                    </div>
                                </li>

                                {/* 名称 */}
                                <li className={styles.columnStyle} style={{ marginBottom: "20px" }}>
                                    <div className={styles.formHelperText}>
                                        <PuiTypography> {t("タイトル")}</PuiTypography>
                                    </div>
                                    <div className={styles.form}>
                                        <TextField
                                            color="secondary"
                                            id="TemplateName"
                                            placeholder={String(t("タイトルを入力してください"))}
                                            autoComplete="off"
                                            value={uploadTemplateName}
                                            onChange={ChangeName}
                                            className={classes.input}
                                        />
                                    </div>
                                </li>

                                {/* 種別 */}
                                <li className={styles.columnStyle}>
                                    <div className={styles.formHelperText}>
                                        <PuiTypography> {t("種類")}</PuiTypography>
                                    </div>
                                    <div className={styles.form}>
                                        {/* <Select
                                            color="secondary"
                                            id="selectTemplateType"
                                            defaultValue="OP"
                                            value={templateType}
                                            onChange={selectTemplateType}
                                            className={classes.select}
                                        >
                                            <MenuItem value={"OP"}>OP</MenuItem>
                                            <MenuItem value={"ED"}>ED</MenuItem>
                                        </Select> */}
                                        <div
                                            className={styles.select}
                                            onClick={() => setIsOpenSelect(!isOpenSelect)}
                                        >
                                            <PuiTypography>
                                                {templateType === "OP"
                                                    ? t("オープニング")
                                                    : t("エンディング")}
                                            </PuiTypography>
                                            {isOpenSelect ? (
                                                <ArrowDropUp className={styles.dropDownicon} />
                                            ) : (
                                                <ArrowDropDown className={styles.dropDownicon} />
                                            )}
                                        </div>
                                        {isOpenSelect && (
                                            <>
                                                <div className={styles.selectMenu}>
                                                    <div onClick={() => selectTemplateType("OP")}>
                                                        <PuiTypography>
                                                            {t("オープニング")}
                                                        </PuiTypography>
                                                    </div>
                                                    <div onClick={() => selectTemplateType("ED")}>
                                                        <PuiTypography>
                                                            {t("エンディング")}
                                                        </PuiTypography>
                                                    </div>
                                                </div>
                                                <div
                                                    className={styles.selectBg}
                                                    onClick={() => setIsOpenSelect(false)}
                                                ></div>
                                            </>
                                        )}
                                    </div>
                                </li>
                            </form>
                        </FormControl>
                        <Button
                            disabled={isBtnActive}
                            size="small"
                            variant="contained"
                            className={`${classes.btnStyle} ${
                                !isBtnActive ? classes.btnStyleActive : ""
                            }`}
                            onClick={handleCreateTemplate}
                        >
                            {t("テンプレート追加")}
                        </Button>
                    </ul>
                </div>
            </Modal>

            {/* ユーザ入力保存確認モーダル */}
            <OnLeaveDialog
                openDialog={openDialog}
                dialogTitle={t("このページを移動しますか？")}
                dialogContent={t("ページを移動すると、入力した内容は失われます")}
                dialogActions={t("閉じる")}
                handleCloseDialog={handleCloseDialog}
                handleCancel={handleCancel}
                handleConfirm={handleConfirm}
            />
        </>
    );
}

export default TemplateCreateModal;
